/**
 * Props for the TimePicker component
 */
export interface TimePickerProps {
  /** The day identifier (e.g., "lundi", "mardi") */
  day: string;
  /** The period identifier (e.g., "matin", "midi", "apres") */
  period: string;
  /** The label to display (e.g., "Début", "Fin") */
  label: string;
  /** The field name in the form ("start" or "end") */
  fieldName: "start" | "end";
  /** Filtered time options for the picker */
  options?: string[];
}

/**
 * Props for the ChildrenSelector component
 */
export interface ChildrenSelectorProps {
  /** The day identifier (e.g., "lundi", "mardi") */
  day: string;
  /** The period identifier (e.g., "matin", "midi", "apres") */
  period: string;
}

/**
 * Props for the PeriodSchedule component
 */
export interface PeriodScheduleProps {
  /** The day identifier (e.g., "lundi", "mardi") */
  day: string;
  /** The period key (e.g., "matin", "midi", "apres") */
  periodKey: string;
  /** The period label to display (e.g., "Matin", "Midi", "Après") */
  periodLabel: string;
  /** Filtered start time options for the period */
  startTimeOptions?: string[];
}

/**
 * Props for the DaySchedule component
 */
export interface DayScheduleProps {
  day: {
    /** Unique identifier for the day */
    id: string;
    /** Display label for the day */
    label: string;
  };
}

/**
 * Props for the DateSelector component
 */
export interface DateSelectorProps {
  /** Current start date value */
  startDateValue: string;
  /** Current end date value */
  endDateValue: string;
  /** Callback when start date changes */
  onStartDateChange: (value: string) => void;
  /** Callback when end date changes */
  onEndDateChange: (value: string) => void;
}

/**
 * Ref interface for the DateSelector component
 */
export interface DateSelectorRef {
  /** Scroll to the date selector */
  scrollIntoView: () => void;
}

/**
 * Available time options for opening hours selection
 */
export const TIME_OPTIONS = [
  "-",
  "08h00",
  "08h15",
  "08h30",
  "08h45",
  "09h00",
  "09h15",
  "09h30",
  "09h45",
  "10h00",
  "10h15",
  "10h30",
  "10h45",
  "11h00",
  "11h15",
  "11h30",
  "11h45",
  "12h00",
  "12h15",
  "12h30",
  "12h45",
  "13h00",
  "13h15",
  "13h30",
  "13h45",
  "14h00",
  "14h15",
  "14h30",
  "14h45",
  "15h00",
  "15h15",
  "15h30",
  "15h45",
  "16h00",
  "16h15",
  "16h30",
  "16h45",
  "17h00",
  "17h15",
  "17h30",
  "17h45",
  "18h00",
  "18h15",
  "18h30",
  "18h45",
  "19h00",
  "19h15",
  "19h30",
  "19h45",
  "20h00",
];

/**
 * Available days of the week with their identifiers and labels
 */
export const DAYS = [
  { id: "lundi", label: "Lundi" },
  { id: "mardi", label: "Mardi" },
  { id: "mercredi", label: "Mercredi" },
  { id: "jeudi", label: "Jeudi" },
  { id: "vendredi", label: "Vendredi" },
  { id: "samedi", label: "Samedi" },
  { id: "dimanche", label: "Dimanche" },
];

/**
 * Filters time options to ensure end time is after start time.
 * @param startTime The selected start time.
 * @returns Filtered time options for end time.
 */
export function filterEndTimeOptions(startTime: string): string[] {
  // If no start time is selected or it's "-", return all options
  if (!startTime || startTime === "-") {
    return TIME_OPTIONS;
  }

  const startIndex = TIME_OPTIONS.indexOf(startTime);
  // If start time is not found, return all options
  if (startIndex === -1) {
    return TIME_OPTIONS;
  }

  // Return options that come after the start time
  return TIME_OPTIONS.filter((_, index) => index === 0 || index > startIndex);
}

/**
 * Filters time options to ensure coherence between periods (morning, noon, afternoon).
 * @param previousEndTime The end time of the previous period.
 * @returns Filtered time options for the start time of the next period.
 */
export function filterNextPeriodStartOptions(previousEndTime: string): string[] {
  // If no previous end time is selected or it's "-", return all options
  if (!previousEndTime || previousEndTime === "-") {
    return TIME_OPTIONS;
  }

  const endIndex = TIME_OPTIONS.indexOf(previousEndTime);
  // If end time is not found, return all options
  if (endIndex === -1) {
    return TIME_OPTIONS;
  }

  // Return options that come after or at the same time as the previous end time (allows consecutive periods)
  return TIME_OPTIONS.filter((_, index) => index === 0 || index >= endIndex);
}
